import type { ReadingSample } from '../types/domain'
import type { SensorInfo, SensorTransport } from './transport'

function seedOf(text: string): number {
  return Array.from(text).reduce((value, char) => Math.imul(value ^ char.charCodeAt(0), 16777619), 2166136261) >>> 0
}

function randomFactory(seed: number): () => number {
  let state = seed || 1
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0
    return state / 0xffffffff
  }
}

export class MockSensorTransport implements SensorTransport {
  readonly kind = 'mock' as const
  readonly supported = true
  private serial = 'NR-S-DEMO'
  private connected = false

  async requestDevice(serialHint?: string): Promise<void> {
    this.serial = serialHint ?? this.serial
    await delay(150)
  }

  async connect(): Promise<void> {
    await delay(250)
    this.connected = true
  }

  async readInfo(): Promise<SensorInfo> {
    this.ensureConnected()
    return {
      deviceId: `mock:${this.serial}`,
      name: this.serial,
      serialNumber: this.serial,
      firmware: 'mock-1.5.0',
      batteryPercent: 84,
      protocolVersion: 1,
      recordCount: 180,
      sampleIntervalMs: 120_000,
    }
  }

  async readSamples(onProgress: (completed: number, total?: number) => void): Promise<ReadingSample[]> {
    this.ensureConnected()
    const moments = 90
    const total = moments * 2
    const random = randomFactory(seedOf(this.serial))
    const now = Date.now()
    const samples: ReadingSample[] = []
    for (let sequenceNo = 0; sequenceNo < moments; sequenceNo++) {
      for (const channel of [0, 1]) {
        const sideShift = channel === 0 ? 2.8 * Math.sin(sequenceNo / 8) : -3.4 * Math.cos(sequenceNo / 10)
        const pressureKpa = 34 + 9 * Math.sin(sequenceNo / 11) + 3 * Math.sin(sequenceNo / 3.7)
          + (sequenceNo > 60 && sequenceNo < 70 ? 18 : 0) + sideShift + random() * 1.5
        samples.push({
          sequenceNo,
          channel,
          measuredAt: new Date(now - (moments - sequenceNo) * 120_000).toISOString(),
          rawValue: (pressureKpa + 0.15) / 0.025,
          quality: sequenceNo === 43 && channel === 1 ? 'SUSPECT' : 'GOOD',
        })
      }
      if (sequenceNo % 9 === 0 || sequenceNo === moments - 1) {
        onProgress(samples.length, total)
        await delay(45)
      }
    }
    return samples
  }

  async acknowledge(): Promise<void> { this.ensureConnected() }
  async clear(): Promise<void> { this.ensureConnected() }
  disconnect(): void { this.connected = false }

  private ensureConnected() {
    if (!this.connected) throw new Error('Mock-датчик не подключён')
  }
}

function delay(milliseconds: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds))
}
